import { useRef, useCallback } from 'react';
import { usePostHog } from '@posthog/react';
import { useLocation } from 'react-router-dom';
import getUserId from '../../utils/getUserId';
import cleanPath from '~/utils/cleanPath';
import { assertNever } from '~/utils/assertNever';
import log from '~/utils/log';

type TrackingFunction = (event: TrackingEvent) => void;

type TrackingEvent =
  | {
      name: 'app_event';
      slug: string;
      messageName: string;
      is_conversion: boolean;
      properties: Record<string, any>;
      featureFlags: Record<string, any>;
    }
  | {
      name: 'app_shown';
      slug: string;
    }
  | {
      name: 'wgt_app_list_item_clicked';
      slug: string;
    }
  | { name: 'wgt_app_list_loaded' }
  | { name: 'sap_app_list_loaded' }
  | { name: 'wgt_dismissed' }
  | { name: 'wgt_loaded_daily' }
  | { name: 'wgt_loaded_monthly' }
  | { name: 'wgt_loaded_monthly_total' };

/**
 * Hook that provides a tracking function for PostHog events.
 * Automatically calls identify() once before the first capture event.
 *
 * @example
 * ```tsx
 * const tracking = useTracking();
 * tracking('button_clicked', { button_name: 'Submit' });
 * ```
 */
const useTracking = (): TrackingFunction => {
  const posthog = usePostHog();
  const location = useLocation();
  const hasIdentified = useRef<boolean>(false);
  const hasBeganCapture = useRef<boolean>(false);

  const tracking = useCallback<TrackingFunction>(
    (event: TrackingEvent) => {
      if (!posthog) return;

      try {
        // Determine if this is iframe or standalone based on the pathname
        // Path pattern: /-/:accountId/(iframe|s)/apps/:slug
        const isStandalone = location.pathname.includes('/s/');
        const type = isStandalone ? 'sap' : 'wgt';
        const cleanedPath = cleanPath(location.pathname);
        const userId = getUserId();

        const generalProperties = {
          bndl_user_id: userId,
          bndl_widget_type: type,
          bndl_path: cleanedPath,
          bndl_wgt_commit_hash: process.env.COMMIT_HASH || '',
          bndl_wgt_build_date: process.env.BUILD_DATE || '',
        };

        // Call identify() once before the first capture
        if (!hasIdentified.current) {
          posthog.identify(userId);
          hasIdentified.current = true;
        }

        INTERACTION_START: if (!hasBeganCapture.current) {
          switch (event.name) {
            case 'wgt_loaded_daily':
            case 'wgt_loaded_monthly':
            case 'wgt_loaded_monthly_total':
              break INTERACTION_START;
          }

          // Also check URL for deep link indicators
          const urlParams = new URLSearchParams(location.search);
          const hasDeepLinkParams =
            urlParams.has('dhParams') || urlParams.has('bndlData');

          // Determine trigger type
          const triggerType: 'deepLink' | 'command' | 'click' | undefined =
            (() => {
              if (hasDeepLinkParams) return 'deepLink';
              return undefined;
            })();

          posthog.capture('wgt_interaction_start', {
            ...generalProperties,
            bndl_trigger_type: triggerType,
            bndl_start_event: event.name,
          });
          hasBeganCapture.current = true;
        }

        switch (event.name) {
          case 'app_event': {
            const prefix = `bndl_event_${event.slug}_`;

            const properties = Object.entries(event.properties).reduce(
              (acc, [key, value]) => {
                if (
                  typeof value !== 'string' &&
                  typeof value !== 'number' &&
                  typeof value !== 'boolean'
                ) {
                  return acc;
                }

                return { ...acc, [`${prefix}__${key}`]: value };
              },
              {} as Record<string, string | number | boolean>,
            );

            posthog.capture(`app_event_${event.slug}__${event.messageName}`, {
              ...properties,
              ...event.featureFlags,
              bndl_event_type: 'app_event',
              bndl_is_conversion: event.is_conversion,
              bndl_slug: event.slug,
              ...generalProperties,
            });

            posthog.capture('app_event', {
              ...properties,
              [`${prefix}_name`]: event.messageName,
              ...event.featureFlags,
              bndl_event_type: 'app_event_general',
              bndl_is_conversion: event.is_conversion,
              bndl_slug: event.slug,
              ...generalProperties,
            });
            break;
          }

          case 'wgt_app_list_item_clicked':
            posthog.capture('wgt_app_list_item_clicked', {
              bndl_slug: event.slug,
              ...generalProperties,
            });
            break;

          case 'app_shown':
            posthog.capture('app_shown', {
              bndl_slug: event.slug,
              ...generalProperties,
            });
            break;
          case 'sap_app_list_loaded':
          case 'wgt_app_list_loaded':
          case 'wgt_dismissed':
          case 'wgt_loaded_daily':
          case 'wgt_loaded_monthly':
          case 'wgt_loaded_monthly_total':
            posthog.capture(event.name, {
              ...generalProperties,
            });
            break;

          default:
            assertNever(event);
        }
      } catch (error) {
        log(`Error capturing event: ${event.name}`, error);
      }
    },
    [posthog, location.pathname],
  );

  return tracking;
};

export default useTracking;
